"use client"

import { useState, useEffect, useMemo } from "react"
import { CloseIcon } from "@/components/icons/pixel-icons"
import { getShopWindowItems } from "@/data/item-types"
import { ShopItemCard } from "@/components/ui/item-card"
import type { ItemDefinition, HeroInventory } from "@/types/item.types"
import { hasInventorySpace, canStackItem, getCloneChargeCostByLevel } from "@/systems/item-system"
import { playBuySound } from "@/hooks/use-sound-effects"

interface StarMapShopWindowProps {
  isOpen: boolean
  money: number
  inventory: HeroInventory
  heroLevel: number
  onClose: () => void
  onBuyItem: (itemDefId: string, price: number) => void
  onBuyCloneCharge: (cost: number) => void
}

export const StarMapShopWindow = ({
  isOpen,
  money,
  inventory,
  heroLevel,
  onClose,
  onBuyItem,
  onBuyCloneCharge,
}: StarMapShopWindowProps) => {
  const [shopItems, setShopItems] = useState<ItemDefinition[]>([])
  const [soldItems, setSoldItems] = useState<Set<string>>(new Set())

  // Calculate clone charge cost based on hero level
  const cloneChargeCost = useMemo(() => getCloneChargeCostByLevel(heroLevel), [heroLevel])

  useEffect(() => {
    if (isOpen) {
      setShopItems(getShopWindowItems())
      setSoldItems(new Set())
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop - renders ON TOP of star map (z-[100]) */}
      <div
        className="fixed inset-0 bg-black/70 z-[110] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Shop Window */}
      <div className="fixed inset-0 flex items-center justify-center z-[111] p-4 pointer-events-none">
        <div className="bg-gradient-to-b from-amber-950/95 to-slate-950/95 border-2 border-amber-500/50 rounded-xl shadow-2xl shadow-amber-500/20 max-w-md w-full pointer-events-auto animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="relative p-4 border-b border-amber-500/30">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-amber-300 hover:text-white transition p-1"
            >
              <CloseIcon size={24} />
            </button>

            <div className="text-center">
              <h2 className="text-2xl font-bold text-amber-200 mb-1">Space Trader</h2>
              <p className="text-amber-300/70 text-xs">Quality goods at fair prices</p>
            </div>

            {/* Gold display */}
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/30 rounded-lg px-3 py-1.5">
              <img
                src="/images/icon-coins.png"
                alt="gold"
                className="w-5 h-5"
                style={{ imageRendering: "pixelated" }}
              />
              <span className="text-white font-bold">{money}</span>
            </div>
          </div>

          {/* Items - REGULAR PRICES (no markup) */}
          <div className="p-4">
            <div className="flex gap-3 justify-center">
              {shopItems.map((item) => {
                const isSold = soldItems.has(item.id)
                const isCloneCharge = item.id === "clone-charge"
                // Clone charge uses dynamic cost based on beacon charges
                const itemPrice = isCloneCharge ? cloneChargeCost : item.cost
                const canAfford = money >= itemPrice
                const canPickup = isCloneCharge || hasInventorySpace(inventory) || canStackItem(inventory, item.id)

                // Clone charge is never marked as sold - it remains available with increasing price
                if (isSold && !isCloneCharge) {
                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-center bg-slate-900/80 rounded-md border-2 border-slate-700/50"
                      style={{ width: 110, minHeight: 140 }}
                    >
                      <span className="text-slate-500 text-xs font-bold">SOLD</span>
                    </div>
                  )
                }

                return (
                  <ShopItemCard
                    key={item.id}
                    itemDef={item}
                    price={itemPrice}
                    canAfford={canAfford}
                    canPickup={canPickup}
                    onBuy={() => {
                      playBuySound()
                      if (isCloneCharge) {
                        // Clone charge: add charge to existing beacon, don't mark as sold
                        onBuyCloneCharge(cloneChargeCost)
                      } else {
                        onBuyItem(item.id, item.cost)
                        setSoldItems(prev => new Set(prev).add(item.id))
                      }
                    }}
                  />
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
